import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

// AI page images
import heroLeft from "@/assets/ai/hero-left.png";
import heroRight from "@/assets/ai/hero-right.png";
import bronzeImg from "@/assets/ai/bronze.png";
import silverImg from "@/assets/ai/silver.png";
import goldImg from "@/assets/ai/gold.png";
import splitTestGuide from "@/assets/ai/split-testing-guide.png";
import lifestyleGuide from "@/assets/ai/lifestyle-guide.png";
import whySplit3 from "@/assets/ai/why-split-test-3.png";
import whySplit4 from "@/assets/ai/why-split-test-4.png";
import whySplit5 from "@/assets/ai/why-split-test-5.png";
import whySplitDesktop from "@/assets/ai/why-split-test-desktop.png";
import cvrExplained from "@/assets/ai/cvr-explained.png";
import creativeChart from "@/assets/ai/creative-chart.png";
import weCreateVolume from "@/assets/ai/we-create-volume.png";
import aiHeadache1 from "@/assets/ai/ai-headache-1.png";
import aiHeadache2 from "@/assets/ai/ai-headache-2.png";
import aiHeadache3 from "@/assets/ai/ai-headache-3.png";
import aiToolsBadge1 from "@/assets/ai/ai-tools-badge-1.png";
import promptHoursDesktop from "@/assets/ai/prompt-hours-desktop.png";
import ctrCvrBanner from "@/assets/ai/ctr-cvr-banner.png";
import ctrCvrDesktop from "@/assets/ai/ctr-cvr-desktop.png";
import howItWorksLeft from "@/assets/ai/how-it-works-left.png";
import howItWorksSteps from "@/assets/ai/how-it-works-steps.png";

declare global {
  interface Window {
    CommonNinja?: { restart?: () => void };
  }
}

const AiServices = () => {
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    const src = "https://cdn.commoninja.com/sdk/latest/commonninja.js";
    if (!document.querySelector(`script[src="${src}"]`)) {
      const script = document.createElement("script");
      script.src = src;
      script.defer = true;
      script.onload = () => {
        setTimeout(() => window.CommonNinja?.restart?.(), 300);
      };
      document.body.appendChild(script);
    } else {
      // Script already loaded, just restart widgets
      setTimeout(() => window.CommonNinja?.restart?.(), 300);
    }
  }, []);

  useEffect(() => {
    if (showMore) {
      setTimeout(() => window.CommonNinja?.restart?.(), 200);
      setTimeout(() => window.CommonNinja?.restart?.(), 600);
    }
  }, [showMore]);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "Montserrat, Helvetica, sans-serif" }}>
      <Navbar />

      {/* Hero Section */}
      <section className="bg-white pt-10 pb-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-3xl md:text-[46px] font-bold text-black leading-tight mb-4">
            Done-For-You AI Product Photos{" "}
            <span className="font-bold">Built for <em>Conversions</em></span>
          </h1>
          <p className="text-lg md:text-xl text-black mb-1">
            AI product photos <strong>cost less</strong>, speed up split testing, and improve data-driven decisions.
          </p>
          <p className="text-lg md:text-xl text-black font-bold">
            We turn real product photos into high-converting AI images built for split testing.
          </p>
        </div>
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 mt-10 items-center">
          <div className="flex justify-center">
            <img src={heroLeft} alt="AI Product Photos" className="max-w-[400px] w-full" />
          </div>
          <div className="flex justify-center">
            <img src={heroRight} alt="3 Easy Steps" className="w-full" />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-white pb-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-[34px] font-bold text-black leading-tight mb-4">
            Lock in your AI photoshoot before spots fill up.
          </h2>
          <p className="text-lg md:text-xl text-black mb-8">
            Use AI to create more product content <em>without wasting time </em>learning new tools.
          </p>
          <button
            onClick={() => document.getElementById("scroll-Pricing")?.scrollIntoView({ behavior: "smooth" })}
            className="bg-[#01c720] hover:bg-[#019e19] text-white font-normal text-lg px-10 py-3 rounded-full transition tracking-wide"
          >
            See Pricing
          </button>
        </div>
      </section>

      {/* Portfolio Section */}
      <section className="bg-white pt-12 pb-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-lg md:text-xl text-black mb-2">Our Portfolio</p>
          <h2 className="text-3xl md:text-[46px] font-bold text-black leading-tight mb-10">
            We don't create images.{" "}
            <br className="hidden md:block" />
            We create conversion assets.
          </h2>
          <div className="commonninja_component pid-87e3c485-36f9-4ac0-b986-753dcbf3c4ac"></div>

{!showMore && (
            <button
              onClick={() => setShowMore(true)}
              className="mt-10 bg-[#01c720] hover:bg-[#019e19] text-white font-normal text-lg px-10 py-3 rounded-full transition tracking-wide"
            >
              Load More
            </button>
          )}

          <div className={`mt-10 ${showMore ? "block" : "hidden"}`}>
            <div className="commonninja_component pid-22806446-a693-418b-b532-58d33d66987d"></div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="scroll-Pricing" className="bg-black py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-[46px] font-bold text-white leading-tight mb-4">
            Affordable AI Product Photoshoots + FREE Split Testing Guide with purchase
          </h2>
          <p className="text-lg md:text-xl text-white mb-10">
            Split test <em>without </em>the investment and AI headaches.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            <a href="https://buy.stripe.com/cNiaEX86Ce8q6TbeqE8IU0b" target="_blank" rel="noopener noreferrer" className="block hover:scale-105 transition-transform">
              <img src={bronzeImg} alt="Bronze AI Pack" className="w-full rounded-lg" />
            </a>
            <a href="https://buy.stripe.com/bJe6oHev0ggydhz0zO8IU0c" target="_blank" rel="noopener noreferrer" className="block hover:scale-105 transition-transform">
              <img src={silverImg} alt="Silver AI Pack" className="w-full rounded-lg" />
            </a>
            <a href="https://buy.stripe.com/28EfZh9aG8O64L32HW8IU0d" target="_blank" rel="noopener noreferrer" className="block hover:scale-105 transition-transform">
              <img src={goldImg} alt="Gold AI Pack" className="w-full rounded-lg" />
            </a>
          </div>
        </div>
      </section>

      {/* Free Bonus Section — Two Columns */}
      <section id="scroll-Payment" className="bg-white py-16 px-4">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <img src={splitTestGuide} alt="Free Split Testing Guide" className="w-full" />
          <img src={lifestyleGuide} alt="Lifestyle Image Conversion Guide" className="w-full" />
        </div>
      </section>

      {/* Why Split Test Section */}
      <section className="bg-white py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-lg md:text-xl text-black mb-2">Why Split Test?</p>
          <h2 className="text-3xl md:text-[46px] font-bold text-black leading-tight mb-4">
            Amazon doesn't reward guessing.
            <br />It rewards testing.
          </h2>
          <p className="text-lg md:text-xl text-black mb-10">
            Your main image controls clicks. If you're not split testing, <strong>you're <em>wasting </em>ad spend</strong>.
          </p>

          {/* Mobile — 3 stacked images */}
          <div className="md:hidden space-y-6">
            <img src={whySplit3} alt="Split Test Comparison" className="w-full" />
            <img src={whySplit4} alt="Split Test Comparison" className="w-full" />
            <img src={whySplit5} alt="Split Test Comparison" className="w-full" />
          </div>
          {/* Desktop — single wide image */}
          <div className="hidden md:block">
            <img src={whySplitDesktop} alt="Why Split Test" className="w-full" />
          </div>
        </div>
      </section>

      {/* Creative Volume Section */}
      <section className="bg-white py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-lg md:text-xl text-black mb-0">You don't need more budget.</p>
          <h2 className="text-3xl md:text-[46px] font-bold text-black leading-tight mb-10">
            You need more creative volume.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 items-center">
            <img src={cvrExplained} alt="CVR Explained" className="w-full" />
            <img src={creativeChart} alt="Creative Volume Chart" className="w-full" />
          </div>

          <p className="text-lg font-bold text-black mb-10">
            More Variations &nbsp;➜&nbsp; More Experiments &nbsp;➜&nbsp; More Optimization &nbsp;➜&nbsp; More Profit
          </p>

          <button
            onClick={() => document.getElementById("scroll-Pricing")?.scrollIntoView({ behavior: "smooth" })}
            className="bg-[#01c720] hover:bg-[#019e19] text-white font-normal text-lg px-10 py-3 rounded-full transition tracking-wide"
          >
            See Pricing
          </button>
        </div>
      </section>

      {/* We Create the Volume Section */}
      <section className="bg-white py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-[46px] font-bold text-black leading-tight mb-8">
            We create the volume
          </h2>
          <img src={weCreateVolume} alt="We Create the Volume" className="w-full mb-10" />
        </div>
      </section>

      {/* AI Headache Section */}
      <section className="bg-white py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-lg md:text-xl text-black mb-2">The Problem with AI</p>
          <h2 className="text-3xl md:text-[46px] font-bold text-black leading-tight mb-2">
            AI Tools can be headache-inducing
          </h2>
          <p className="text-base text-black mb-10">
            We know the struggle. Wasting hours and money on different AI tools with nothing to show for it—except for maybe three arms.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <img src={aiHeadache1} alt="Bad AI Example 1" className="w-full rounded-lg" />
            <img src={aiHeadache2} alt="Bad AI Example 2" className="w-full rounded-lg" />
            <img src={aiHeadache3} alt="Bad AI Example 3" className="w-full rounded-lg" />
          </div>

          {/* Mobile badge */}
          <div className="md:hidden mb-6">
            <img src={aiToolsBadge1} alt="AI Tools Issues" className="w-full mx-0 pl-[50px] pr-[50px]" />
          </div>
          {/* Desktop badge */}
          <div className="hidden md:flex justify-center mb-6">
            <img src={promptHoursDesktop} alt="AI Tools Issues" className="w-full" />
          </div>

          <p className="text-xl md:text-[28px] text-black mb-8 leading-relaxed">
            That's why we offer this service—<em>to help brands that want volume, speed, and great creative at a low cost</em>—so you can start split testing next week.
          </p>

          {/* Mobile banner */}
          <div className="md:hidden">
            <img src={ctrCvrBanner} alt="CTR CVR Banner" className="w-full" />
          </div>
          {/* Desktop banner */}
          <div className="hidden md:block mt-8">
            <img src={ctrCvrDesktop} alt="CTR CVR Banner" className="w-full" />
          </div>
        </div>
      </section>

      {/* Working With Us Section */}
      <section className="bg-white py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-[46px] font-bold text-black leading-tight mb-4">
            Working with us is <em>easy</em>
          </h2>
          <p className="text-lg md:text-xl text-black mb-10">
            The Solution to Split Testing + Buying Back Your Time
          </p>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
            <div className="md:col-span-7">
              <img src={howItWorksLeft} alt="How it works" className="w-full" />
            </div>
            <div className="md:col-span-5 text-left">
              <img src={howItWorksSteps} alt="Steps" className="w-full mb-6" />
              <p className="text-lg md:text-xl text-black mb-2">
                AI becomes your <em>testing </em>engine.
              </p>
              <p className="text-lg md:text-xl text-black mb-8">
                Real shoots become your <em>scaling </em>engine.
              </p>
              <button
                onClick={() => document.getElementById("scroll-Pricing")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-[#01c720] hover:bg-[#019e19] text-white font-normal text-lg px-10 py-3 rounded-full transition tracking-wide"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="bg-white pt-20 pb-8 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-[46px] font-bold text-black leading-tight mb-10">
            What our clients have to say
          </h2>
          <div className="commonninja_component pid-1e622c7b-461c-4f31-b6cf-464ad7efb4da"></div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="bg-[#f5f5f5] py-12 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-[46px] font-bold text-black leading-tight mb-8">
            FAQs
          </h2>
          <div className="commonninja_component pid-fabfe05f-ceea-4b10-bd89-f38725b9419d"></div>
          <button
            onClick={() => document.getElementById("scroll-Pricing")?.scrollIntoView({ behavior: "smooth" })}
            className="mt-10 w-full bg-[#01c720] hover:bg-[#019e19] text-white font-normal text-lg py-3 rounded-full transition tracking-wide"
          >
            Get Started
          </button>
        </div>
      </section>

      {/* Contact Section */}
      <section id="scroll-Contact" className="bg-white py-12 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-[46px] font-bold text-black leading-tight mb-4">
            Have questions? Reach out anytime.
          </h2>
          <p className="text-lg text-black mb-8">
            Call us at{" "}
            <a href="tel:719-257-3557" className="text-[#0077cc] hover:underline">719-257-3557</a>
            {" "}or send an email{" "}
            <a href="mailto:ian@evolvemediaagency.com" className="text-[#0077cc] hover:underline">ian@EvolveMediaAgency.com</a>
          </p>
          <a
            href="https://calendly.com/evolvemedia/amz-30-min"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-[#01c720] hover:bg-[#019e19] text-white font-normal text-lg px-10 py-3 rounded-full transition tracking-wide animate-wobble"
          >
            Book a Call
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AiServices;
